import {forwardRef, useImperativeHandle, useRef} from 'react';

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 130;
// Raw colors for HTML canvas — the 2D context API does not accept platform
// theme tokens, so values are pinned here and kept aligned with the design
// system on a best-effort basis.
const STROKE_COLOR = '#1a365d';
const BORDER_COLOR = '#e2e8f0';
const BACKGROUND_COLOR = '#fafafa';
const STROKE_WIDTH = 2.2;
const ALPHA_THRESHOLD = 10;
const DISABLED_OPACITY = 0.6;

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  clear: () => void;
  getBlob: () => Promise<Blob | null>;
}

interface SignaturePadProps {
  isDisabled?: boolean;
  ariaLabel: string;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad(props, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDownRef = useRef(false);

    const getContext = () => canvasRef.current?.getContext('2d') ?? null;

    const getPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return null;
      }
      const rect = canvas.getBoundingClientRect();
      const sx = canvas.width / rect.width;
      const sy = canvas.height / rect.height;
      return {
        x: (event.clientX - rect.left) * sx,
        y: (event.clientY - rect.top) * sy,
      };
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (props.isDisabled) {
        return;
      }
      const ctx = getContext();
      const point = getPoint(event);
      if (!ctx || !point) {
        return;
      }
      isDownRef.current = true;
      canvasRef.current?.setPointerCapture(event.pointerId);
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDownRef.current) {
        return;
      }
      const ctx = getContext();
      const point = getPoint(event);
      if (!ctx || !point) {
        return;
      }
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    };

    const stopStroke = () => {
      isDownRef.current = false;
    };

    const setupContext = (canvas: HTMLCanvasElement | null) => {
      canvasRef.current = canvas;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }
      ctx.strokeStyle = STROKE_COLOR;
      ctx.lineWidth = STROKE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    useImperativeHandle(
      ref,
      () => ({
        isEmpty: () => {
          const canvas = canvasRef.current;
          const ctx = getContext();
          if (!canvas || !ctx) {
            return true;
          }
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          for (let i = 3; i < data.length; i += 4) {
            if ((data.at(i) ?? 0) > ALPHA_THRESHOLD) {
              return false;
            }
          }
          return true;
        },
        clear: () => {
          const canvas = canvasRef.current;
          const ctx = getContext();
          if (!canvas || !ctx) {
            return;
          }
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        },
        getBlob: () =>
          new Promise((resolve) => {
            const canvas = canvasRef.current;
            if (!canvas) {
              resolve(null);
              return;
            }
            canvas.toBlob((blob) => resolve(blob), 'image/png');
          }),
      }),
      []
    );

    return (
      <canvas
        ref={setupContext}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        aria-label={props.ariaLabel}
        role="img"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopStroke}
        onPointerLeave={stopStroke}
        onPointerCancel={stopStroke}
        // eslint-disable-next-line react/forbid-dom-props -- raw <canvas> has no platform primitive
        style={{
          display: 'block',
          width: '100%',
          height: CANVAS_HEIGHT,
          border: `1.5px solid ${BORDER_COLOR}`,
          borderRadius: 8,
          background: BACKGROUND_COLOR,
          cursor: props.isDisabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none',
          opacity: props.isDisabled ? DISABLED_OPACITY : 1,
        }}
      />
    );
  }
);
